import { ExtendedQualQuestion, Lot, QualQuestion, QualQuestionAppliedState } from 'types';
import { defaultLotQuestion } from './defaultLotQuestion';

/**
 * Type guard to check if a QualQuestion has the additional properties needed for a Lot question
 *
 * @param question The question to check
 * @returns Whether the question has the additional properties
 */
const isExtendedQualQuestion = (question: QualQuestion): question is ExtendedQualQuestion => {
  return (
    'answer_options' in question &&
    'group_id' in question &&
    'qual_question_answer_options' in question &&
    'questionable_id' in question
  );
};

/**
 * Get unique values from an array based on a specific key
 *
 * @param array - The array to extract unique values from
 * @param key - The property key to use for determining uniqueness
 * @returns An array with unique values based on the specified key
 */
const getUniqueByKey = <T, K extends keyof T>(array: T[], key: K): T[] => {
  const seen = new Set();
  return array.filter(item => {
    const value = item[key];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

/**
 * Generates survey questions for a lot and returns the questions and their applied state map
 *
 * @param lot {Lot} - The lot to generate questions for
 * @param questions {QualQuestion[]} - All available qual questions
 * @returns {[QualQuestion[], Record<string, QualQuestionAppliedState>]} A tuple containing the new lot questions and the applied state map
 */
export const generateQuestionsForLot = (
  lot: Lot,
  questions: QualQuestion[]
): [QualQuestion[], Record<string, QualQuestionAppliedState>] => {
  // Filter questions that are applicable to lots
  const lotQuestions: ExtendedQualQuestion[] = questions.filter(
    (q): q is ExtendedQualQuestion => q.questionable_type === 'Lot' && isExtendedQualQuestion(q)
  );

  // Ensure we only have unique questions based on group_id
  const uniqueLotQuestions = getUniqueByKey(lotQuestions, 'group_id');

  const newLotQuestions = uniqueLotQuestions.map(question => {
    return defaultLotQuestion(lot.id, question);
  });

  const initialValue: Record<string, QualQuestionAppliedState> = {};

  const appliedStateMap = newLotQuestions.reduce(
    (stateMap, question) => ({
      ...stateMap,
      [question.id]: {
        group_id: question.group_id,
        is_applied: Boolean(question.is_applied)
      }
    }),
    initialValue
  );

  return [newLotQuestions, appliedStateMap];
};
